import { type RefObject, useId, useState } from 'react';

import { Button } from '@/components/Button/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/Dialog/Dialog';
import { Field } from '@/components/Field/Field';
import { Textarea } from '@/components/Textarea/Textarea';
import { type XeroSyncFinding } from '@/features/reports/xero/api';
import { XERO_SYNC_COPY } from '@/features/reports/xero/copy';
import { sideText } from '@/features/reports/xero/labels';

const C = XERO_SYNC_COPY.dialog;

/** The API keeps a reason to this many characters. */
export const REASON_MAX = 500;

export interface DismissDialogProps {
    open: boolean;
    /** The difference being dismissed; kept while the dialog closes, so it does not empty as it goes. */
    finding: XeroSyncFinding | null;
    onOpenChange: (open: boolean) => void;
    /** The button that opened it, which gets the keyboard back when it closes. */
    returnFocusTo: RefObject<HTMLElement | null>;
    pending: boolean;
    /** Why the last try was refused, in the API's words: the figures changed, or it no longer differs. */
    refused: string | null;
    onConfirm: (reason: string) => void;
}

/**
 * Dismiss (ALS-388): the difference as the person sees it, both figures, and why it can be left as
 * it is. The reason is required and kept with their name. What they typed stays when the API refuses
 * it, with why, so nothing has to be typed twice. Escape and Cancel close it unless it is saving, and
 * closing hands the keyboard back to the button that opened it, which is why it stays mounted.
 */
export function DismissDialog({
    open,
    finding,
    onOpenChange,
    returnFocusTo,
    pending,
    refused,
    onConfirm,
}: DismissDialogProps) {
    return (
        <Dialog open={open} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
            {finding ? (
                // A fresh reason for each difference opened.
                <DismissForm
                    key={finding.id}
                    finding={finding}
                    onCancel={() => onOpenChange(false)}
                    returnFocusTo={returnFocusTo}
                    pending={pending}
                    refused={refused}
                    onConfirm={onConfirm}
                />
            ) : null}
        </Dialog>
    );
}

function DismissForm({
    finding,
    onCancel,
    returnFocusTo,
    pending,
    refused,
    onConfirm,
}: {
    finding: XeroSyncFinding;
    onCancel: () => void;
    returnFocusTo: RefObject<HTMLElement | null>;
    pending: boolean;
    refused: string | null;
    onConfirm: (reason: string) => void;
}) {
    const [reason, setReason] = useState('');
    const [tried, setTried] = useState(false);
    const hintId = useId();
    const trimmed = reason.trim();
    const problem =
        trimmed.length === 0
            ? C.required
            : trimmed.length > REASON_MAX
              ? C.tooLong(REASON_MAX)
              : null;

    const submit = () => {
        setTried(true);
        if (!problem) onConfirm(trimmed);
    };

    return (
        // As Add address does (ALS-406): the keyboard goes back to the row's Dismiss, which a
        // list that re-renders under the dialog would otherwise lose to the page.
        <DialogContent
            onCloseAutoFocus={(event) => {
                event.preventDefault();
                returnFocusTo.current?.focus();
            }}
        >
            <DialogHeader>
                <DialogTitle>{C.title}</DialogTitle>
                <DialogDescription>{C.body}</DialogDescription>
            </DialogHeader>
            <dl className="bg-muted grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-md p-3 text-sm">
                <dt className="text-muted-foreground">{XERO_SYNC_COPY.columns.type}</dt>
                <dd className="min-w-0 [overflow-wrap:anywhere]">
                    {XERO_SYNC_COPY.kinds[finding.kind]}
                    {finding.document ? ` · ${finding.document}` : ''}
                </dd>
                <dt className="text-muted-foreground">{XERO_SYNC_COPY.columns.admin}</dt>
                <dd className="min-w-0 [overflow-wrap:anywhere]">
                    {sideText(finding.admin, finding.currency)}
                </dd>
                <dt className="text-muted-foreground">{XERO_SYNC_COPY.columns.xero}</dt>
                <dd className="min-w-0 [overflow-wrap:anywhere]">
                    {sideText(finding.xero, finding.currency)}
                </dd>
            </dl>
            <form
                id={`${hintId}-form`}
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <Field
                    label={C.reason}
                    required
                    error={(tried && !!problem) || !!refused}
                    message={(tried && problem) || refused || undefined}
                >
                    {(control) => (
                        <Textarea
                            {...control}
                            value={reason}
                            maxLength={REASON_MAX + 50}
                            placeholder={C.placeholder}
                            disabled={pending}
                            onChange={(event) => setReason(event.target.value)}
                            className="min-h-24"
                        />
                    )}
                </Field>
            </form>
            <DialogFooter>
                <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
                    {C.cancel}
                </Button>
                <Button size="sm" type="submit" form={`${hintId}-form`} loading={pending}>
                    {C.confirm}
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}
